import { apiCall } from 'src/utils/ApiCall';
import { GetControlPivotDataOutput } from '@amzn/controllershiphub-typescript-client';
import { CONTROL_APIS, ApiError, MAX_PIVOT_API_ITERATIONS } from 'src/common/config/ApiConstants';

interface PivotDataResponse {
  rowData: object[];
  pivotResultFields: string[];
  rowCount: number | undefined;
}

export const getControlPivotData = async (
  controlId: string,
  filters: Record<string, string[]> = {},
  aggregationValues: string[],
  rowAggregationField: string,
  columnAggregationFields: string[],
  groupKeys: string[]
): Promise<PivotDataResponse> => {
  try {
    // set up request params
    const body: Record<string, string | string[]> = {};
    if (Object.keys(filters).length > 0) {
      body.filters = JSON.stringify(filters);
      body.groupKeys = groupKeys;
    }
    let nextToken = null;
    let iterationCount = 0;
    let results = [];
    let pivotResultFields: string[] = [];
    let rowCount: number | undefined = undefined;

    // call API iteratively
    do {
      // Final API call will have a null nextToken
      if (nextToken != null) {
        body.nextToken = nextToken;
      }

      const response = await apiCall<GetControlPivotDataOutput>({
        endpoint: CONTROL_APIS.ENDPOINTS.PIVOT_DATA(controlId),
        method: 'POST',
        body: body,
      });
      nextToken = response.nextToken;
      results.push(
        ...(response.rowData?.map((row) => {
          return JSON.parse(row);
        }) ?? [])
      );

      // Do not reset the pivotResultFields on the final call which will have an empty pivotResultFields response
      if (pivotResultFields.length === 0 && response.pivotResultFields != undefined) {
        pivotResultFields = response.pivotResultFields;
      }

      rowCount = response.rowCount ?? rowCount;
      iterationCount++;
    } while (nextToken != null && iterationCount < MAX_PIVOT_API_ITERATIONS);

    // TODO: Process the results to handle rows that are split between pages

    return {
      rowData: results,
      pivotResultFields: pivotResultFields,
      rowCount: rowCount,
    };
  } catch (err: unknown) {
    const error = err as Error;
    if (error instanceof ApiError && error.status === 401) {
      throw new Error(CONTROL_APIS.ERROR_MESSAGES.UNAUTHORIZED);
    }
    throw new Error(CONTROL_APIS.ERROR_MESSAGES.DATA_FETCH_FAILED);
  }
};
